// ============================================================
// FILE: src/pages/Contact.jsx
// ============================================================

import React, { useState, useRef } from "react";
import { motion, useInView } from "framer-motion";
import {
    Phone,
    Mail,
    MapPin,
    Send,
    MessageCircle,
    CheckCircle,
    Building2,
    Users,
    Award,
    ExternalLink,
} from "lucide-react";

const Contact = () => {
    const [formData, setFormData] = useState({
        name: "",
        email: "",
        phone: "",
        subject: "",
        message: "",
    });

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSubmitted, setIsSubmitted] = useState(false);

    const sectionRef = useRef(null);

    const isInView = useInView(sectionRef, {
        once: true,
        margin: "-100px",
    });

    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value,
        });
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        setIsSubmitting(true);

        // Simulate API call
        setTimeout(() => {
            setIsSubmitting(false);
            setIsSubmitted(true);

            setFormData({
                name: "",
                email: "",
                phone: "",
                subject: "",
                message: "",
            });

            setTimeout(() => {
                setIsSubmitted(false);
            }, 5000);
        }, 1500);
    };

    // ============================================================
    // CONTACT INFORMATION
    // ============================================================

    const contactInfo = [
        {
            icon: Phone,
            title: "Phone",
            details: "+91 9654446434",
            subDetails: "Contact us for project enquiries",
            color: "from-[var(--color-accent)] to-[var(--color-secondary)]",
            bgColor: "bg-[var(--color-accent)]/10",
            iconColor: "text-[var(--color-accent)]",
        },
        {
            icon: Mail,
            title: "Email",
            details: "contact@sanwariyajisteels.com",
            subDetails: "We'll respond to your enquiry",
            color: "from-purple-500 to-pink-500",
            bgColor: "bg-purple-50",
            iconColor: "text-purple-600",
        },
        {
            icon: MapPin,
            title: "Our Location",
            details: "Plot No. 31A, Sai Complex",
            subDetails:
                "Industrial Area, Wajirpur Road, Sector 87, Greater Faridabad - 121002",
            color: "from-orange-500 to-red-500",
            bgColor: "bg-orange-50",
            iconColor: "text-orange-600",
        },

    ];

    // ============================================================
    // STATS
    // ============================================================

    const stats = [
        {
            icon: Building2,
            value: "100+",
            label: "Projects",
        },
        {
            icon: Users,
            value: "50+",
            label: "Workforce",
        },
        {
            icon: Award,
            value: "10+",
            label: "Years Experience",
        },
    ];

    // ============================================================
    // GOOGLE MAP
    // ============================================================

    const mapAddress =
        "Plot No. 31A, Sai Complex Industrial Area, Wajirpur Road, Sector 87, Greater Faridabad, Haryana 121002";

    const mapUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
        mapAddress
    )}`;

    const embedMapUrl = `https://www.google.com/maps?q=${encodeURIComponent(
        mapAddress
    )}&output=embed`;

    return (
        <div
            ref={sectionRef}
            className="relative min-h-screen overflow-hidden pt-16 sm:pt-20 md:pt-24"
        >
            {/* =====================================================
                BACKGROUND DECORATIONS
            ===================================================== */}

            <div className="pointer-events-none absolute right-0 top-0 h-96 w-96 -translate-y-1/2 translate-x-1/3 rounded-full bg-[var(--color-accent)]/10 blur-3xl" />

            <div className="pointer-events-none absolute bottom-0 left-0 h-80 w-80 -translate-x-1/4 translate-y-1/2 rounded-full bg-[var(--color-secondary)]/10 blur-3xl" />

            <div className="pointer-events-none absolute left-1/2 top-1/2 h-64 w-64 -translate-x-1/2 -translate-y-1/2 rounded-full bg-[var(--color-dark)]/5 blur-3xl" />

            <section className="relative py-10 sm:py-14 md:py-16">
                <div className="container mx-auto max-w-7xl px-4 sm:px-6 relative z-10">

                    {/* =================================================
                        HEADING
                    ================================================= */}

                    <motion.div
                        initial={{
                            opacity: 0,
                            y: 30,
                        }}
                        animate={
                            isInView
                                ? {
                                    opacity: 1,
                                    y: 0,
                                }
                                : {}
                        }
                        transition={{
                            duration: 0.6,
                        }}
                        className="mb-10 text-center md:mb-12"
                    >
                        <motion.span
                            initial={{
                                opacity: 0,
                                scale: 0.8,
                            }}
                            animate={
                                isInView
                                    ? {
                                        opacity: 1,
                                        scale: 1,
                                    }
                                    : {}
                            }
                            transition={{
                                delay: 0.2,
                            }}
                            className="pill inline-flex items-center gap-2"
                        >
                            <MessageCircle size={16} />
                            Get In Touch
                        </motion.span>

                        <h1 className="mt-4 text-3xl font-bold text-slate-800 sm:text-4xl md:text-5xl">
                            Let's{" "}
                            <span className="gradient-text">
                                Connect
                            </span>
                        </h1>

                        <motion.p
                            initial={{
                                opacity: 0,
                            }}
                            animate={
                                isInView
                                    ? {
                                        opacity: 1,
                                    }
                                    : {}
                            }
                            transition={{
                                delay: 0.3,
                            }}
                            className="mx-auto mt-4 max-w-2xl text-sm leading-relaxed text-slate-600 sm:text-base"
                        >
                            Have a steel fabrication or infrastructure
                            requirement? Get in touch with our team and
                            let's discuss your project.
                        </motion.p>

                        <motion.div
                            initial={{
                                width: 0,
                            }}
                            animate={
                                isInView
                                    ? {
                                        width: 70,
                                    }
                                    : {}
                            }
                            transition={{
                                delay: 0.4,
                                duration: 0.8,
                            }}
                            className="mx-auto mt-5 h-1 rounded-full bg-gradient-to-r from-[var(--color-accent)] to-[var(--color-secondary)]"
                        />
                    </motion.div>

                    {/* =================================================
                        CONTACT + FORM
                    ================================================= */}

                    <div className="grid grid-cols-1 gap-7 lg:grid-cols-5 lg:gap-8">

                        {/* =================================================
                            CONTACT INFORMATION
                        ================================================= */}

                        <motion.div
                            initial={{
                                opacity: 0,
                                x: -30,
                            }}
                            animate={
                                isInView
                                    ? {
                                        opacity: 1,
                                        x: 0,
                                    }
                                    : {}
                            }
                            transition={{
                                delay: 0.3,
                            }}
                            className="space-y-3 lg:col-span-2"
                        >
                            {contactInfo.map((info, index) => {
                                const Icon = info.icon;

                                return (
                                    <motion.div
                                        key={index}
                                        initial={{
                                            opacity: 0,
                                            y: 20,
                                        }}
                                        animate={
                                            isInView
                                                ? {
                                                    opacity: 1,
                                                    y: 0,
                                                }
                                                : {}
                                        }
                                        transition={{
                                            delay: 0.4 + index * 0.1,
                                        }}
                                        className="group relative overflow-hidden rounded-[20px] border border-[var(--color-border)]/70 bg-white/80 p-4 shadow-sm backdrop-blur-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-lg sm:p-5"
                                    >
                                        <div className="flex items-start gap-4">

                                            {/* Icon */}

                                            <div
                                                className={`relative flex-shrink-0 rounded-xl p-3 ${info.bgColor} transition-transform duration-300 group-hover:scale-110`}
                                            >
                                                <Icon
                                                    className={`h-5 w-5 ${info.iconColor}`}
                                                />

                                                <div
                                                    className={`absolute inset-0 rounded-xl bg-gradient-to-r ${info.color} opacity-0 transition-opacity duration-300 group-hover:opacity-10`}
                                                />
                                            </div>

                                            {/* Content */}

                                            <div className="min-w-0 flex-1">
                                                <h4 className="text-sm font-semibold text-slate-800 sm:text-base">
                                                    {info.title}
                                                </h4>

                                                <p className="break-words text-sm font-medium text-slate-600 sm:text-base">
                                                    {info.details}
                                                </p>

                                                {info.subDetails && (
                                                    <p className="mt-1 text-xs leading-5 text-slate-400 sm:text-sm">
                                                        {info.subDetails}
                                                    </p>
                                                )}
                                            </div>
                                        </div>
                                    </motion.div>
                                );
                            })}

                            {/* =================================================
                                STATS
                            ================================================= */}

                            <motion.div
                                initial={{
                                    opacity: 0,
                                    y: 20,
                                }}
                                animate={
                                    isInView
                                        ? {
                                            opacity: 1,
                                            y: 0,
                                        }
                                        : {}
                                }
                                transition={{
                                    delay: 0.8,
                                }}
                                className="grid grid-cols-3 gap-2 pt-1"
                            >
                                {stats.map((stat, index) => {
                                    const Icon = stat.icon;

                                    return (
                                        <div
                                            key={index}
                                            className="rounded-[20px] border border-[var(--color-border)]/70 bg-white/80 p-3 text-center shadow-sm backdrop-blur-sm"
                                        >
                                            <Icon className="mx-auto mb-1 h-5 w-5 text-[var(--color-accent)]" />

                                            <div className="text-lg font-bold text-slate-800 sm:text-xl">
                                                {stat.value}
                                            </div>

                                            <div className="text-[10px] text-slate-500 sm:text-xs">
                                                {stat.label}
                                            </div>
                                        </div>
                                    );
                                })}
                            </motion.div>
                        </motion.div>

                        {/* =================================================
                            CONTACT FORM
                        ================================================= */}

                        <motion.div
                            initial={{
                                opacity: 0,
                                x: 30,
                            }}
                            animate={
                                isInView
                                    ? {
                                        opacity: 1,
                                        x: 0,
                                    }
                                    : {}
                            }
                            transition={{
                                delay: 0.4,
                            }}
                            className="lg:col-span-3"
                        >
                            <div className="relative overflow-hidden rounded-[26px] border border-slate-200/60 bg-white/80 p-5 shadow-xl backdrop-blur-sm sm:p-7">

                                {/* Form Header */}

                                <div className="mb-5">
                                    <p className="text-[10px] font-bold tracking-[0.2em] text-[var(--color-accent)]">
                                        PROJECT ENQUIRY
                                    </p>

                                    <h3 className="mt-1 text-xl font-bold text-slate-800 sm:text-2xl">
                                        Send us a{" "}
                                        <span className="gradient-text">
                                            Message
                                        </span>
                                    </h3>

                                    <p className="mt-1 text-xs text-slate-500 sm:text-sm">
                                        Tell us about your project and our team
                                        will get back to you.
                                    </p>
                                </div>

                                <form
                                    onSubmit={handleSubmit}
                                    className="space-y-3.5"
                                >

                                    {/* Name + Email */}

                                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">

                                        <div>
                                            <label className="mb-1.5 block text-xs font-medium text-slate-700">
                                                Your Name{" "}
                                                <span className="text-red-500">
                                                    *
                                                </span>
                                            </label>

                                            <input
                                                type="text"
                                                name="name"
                                                value={formData.name}
                                                onChange={handleChange}
                                                required
                                                className="w-full rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm text-slate-800 outline-none transition-all placeholder:text-slate-400 focus:border-[var(--color-accent)] focus:ring-2 focus:ring-[var(--color-accent)]/20"
                                                placeholder="Your name"
                                            />
                                        </div>

                                        <div>
                                            <label className="mb-1.5 block text-xs font-medium text-slate-700">
                                                Email Address{" "}
                                                <span className="text-red-500">
                                                    *
                                                </span>
                                            </label>

                                            <input
                                                type="email"
                                                name="email"
                                                value={formData.email}
                                                onChange={handleChange}
                                                required
                                                className="w-full rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm text-slate-800 outline-none transition-all placeholder:text-slate-400 focus:border-[var(--color-accent)] focus:ring-2 focus:ring-[var(--color-accent)]/20"
                                                placeholder="you@example.com"
                                            />
                                        </div>
                                    </div>

                                    {/* Phone + Subject */}

                                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">

                                        <div>
                                            <label className="mb-1.5 block text-xs font-medium text-slate-700">
                                                Phone Number
                                            </label>

                                            <input
                                                type="tel"
                                                name="phone"
                                                value={formData.phone}
                                                onChange={handleChange}
                                                className="w-full rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm text-slate-800 outline-none transition-all placeholder:text-slate-400 focus:border-[var(--color-accent)] focus:ring-2 focus:ring-[var(--color-accent)]/20"
                                                placeholder="+91 9654446434"
                                            />
                                        </div>

                                        <div>
                                            <label className="mb-1.5 block text-xs font-medium text-slate-700">
                                                Subject
                                            </label>

                                            <input
                                                type="text"
                                                name="subject"
                                                value={formData.subject}
                                                onChange={handleChange}
                                                className="w-full rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm text-slate-800 outline-none transition-all placeholder:text-slate-400 focus:border-[var(--color-accent)] focus:ring-2 focus:ring-[var(--color-accent)]/20"
                                                placeholder="Project Inquiry"
                                            />
                                        </div>
                                    </div>

                                    {/* Message */}

                                    <div>
                                        <label className="mb-1.5 block text-xs font-medium text-slate-700">
                                            Message{" "}
                                            <span className="text-red-500">
                                                *
                                            </span>
                                        </label>

                                        <textarea
                                            name="message"
                                            value={formData.message}
                                            onChange={handleChange}
                                            required
                                            rows="4"
                                            className="w-full resize-none rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-sm text-slate-800 outline-none transition-all placeholder:text-slate-400 focus:border-[var(--color-accent)] focus:ring-2 focus:ring-[var(--color-accent)]/20"
                                            placeholder="Tell us about your project..."
                                        />
                                    </div>

                                    {/* Submit */}

                                    <button
                                        type="submit"
                                        disabled={isSubmitting}
                                        className="group w-full rounded-xl bg-gradient-to-r from-[var(--color-accent)] to-[var(--color-secondary)] px-6 py-3 text-sm font-semibold text-white transition-all duration-300 hover:scale-[1.01] hover:shadow-lg hover:shadow-[var(--color-accent)]/20 disabled:cursor-not-allowed disabled:opacity-70"
                                    >
                                        <span className="flex items-center justify-center gap-2">

                                            {isSubmitting ? (
                                                <>
                                                    <svg
                                                        className="h-5 w-5 animate-spin"
                                                        viewBox="0 0 24 24"
                                                    >
                                                        <circle
                                                            className="opacity-25"
                                                            cx="12"
                                                            cy="12"
                                                            r="10"
                                                            stroke="currentColor"
                                                            strokeWidth="4"
                                                            fill="none"
                                                        />

                                                        <path
                                                            className="opacity-75"
                                                            fill="currentColor"
                                                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                                        />
                                                    </svg>

                                                    Sending...
                                                </>
                                            ) : isSubmitted ? (
                                                <>
                                                    <CheckCircle size={19} />
                                                    Message Sent!
                                                </>
                                            ) : (
                                                <>
                                                    Send Message

                                                    <Send
                                                        size={17}
                                                        className="transition-transform duration-300 group-hover:translate-x-1"
                                                    />
                                                </>
                                            )}
                                        </span>
                                    </button>

                                    {/* Success Message */}

                                    {isSubmitted && (
                                        <motion.div
                                            initial={{
                                                opacity: 0,
                                                y: 10,
                                            }}
                                            animate={{
                                                opacity: 1,
                                                y: 0,
                                            }}
                                            className="flex items-center gap-2 rounded-lg bg-emerald-50 px-4 py-2 text-xs text-emerald-600 sm:text-sm"
                                        >
                                            <CheckCircle size={17} />

                                            Your message has been sent
                                            successfully!
                                        </motion.div>
                                    )}
                                </form>
                            </div>
                        </motion.div>
                    </div>

                    {/* =================================================
                        LOCATION / MAP
                    ================================================= */}

                    <motion.div
                        initial={{
                            opacity: 0,
                            y: 30,
                        }}
                        animate={
                            isInView
                                ? {
                                    opacity: 1,
                                    y: 0,
                                }
                                : {}
                        }
                        transition={{
                            delay: 0.6,
                        }}
                        className="mt-10 md:mt-12"
                    >
                        <div className="overflow-hidden rounded-[26px] border border-slate-200/70 bg-white p-2 shadow-lg">

                            {/* Map */}

                            <div className="relative h-[280px] overflow-hidden rounded-[20px] sm:h-[320px] md:h-[360px]">

                                <iframe
                                    title="Sanwariya Ji Steels & Infrastructure Location"
                                    src={embedMapUrl}
                                    width="100%"
                                    height="100%"
                                    style={{
                                        border: 0,
                                    }}
                                    loading="lazy"
                                    allowFullScreen
                                    referrerPolicy="no-referrer-when-downgrade"
                                />

                                {/* Location Card */}

                                <div className="absolute bottom-4 left-4 right-4 sm:left-5 sm:right-auto sm:max-w-sm">
                                    <div className="rounded-2xl border border-white/30 bg-white/95 p-4 shadow-xl backdrop-blur-md">

                                        <div className="flex items-start gap-3">

                                            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[var(--color-accent)]/10">
                                                <MapPin
                                                    size={20}
                                                    className="text-[var(--color-accent)]"
                                                />
                                            </div>

                                            <div>
                                                <p className="text-xs font-bold text-slate-800 sm:text-sm">
                                                    Sanwariya Ji Steels &
                                                    Infrastructure LLP
                                                </p>

                                                <p className="mt-1 text-[11px] leading-5 text-slate-500">
                                                    Plot No. 31A, Sai Complex,
                                                    Industrial Area, Wajirpur
                                                    Road, Sector 87, Greater
                                                    Faridabad - 121002
                                                </p>

                                                <a
                                                    href={mapUrl}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="mt-2 inline-flex items-center gap-1 text-[11px] font-semibold text-[var(--color-accent)] hover:underline"
                                                >
                                                    Open in Google Maps

                                                    <ExternalLink size={12} />
                                                </a>
                                            </div>

                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </motion.div>

                </div>
            </section>
        </div>
    );
};

export default Contact;